<template>
  <div
    data-test="graphics-qrCodeSettings"
  >
    <SidebarSettingsGroup
      v-if="!element.data?.noRemap && !editingMultiple"
      title="Content"
      collapsible
      :element-type="element.type"
      data-test="graphics-qrCodeSettings-content"
    >
      <div class="sidebar-form-group">
        <map-data-control
          v-if="dataProvider"
          :data-provider="dataProvider"
          :scroll-offset="scrollOffset"
          :element="element"
          :mapped="isMapped('permalink')"
          property="permalink"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />

        <tracking-link-picker
          v-if="!isMapped('permalink')"
          :model-value="element.trackingLinkRid"
          @update:model-value="updateOneElementValue('trackingLinkRid', $event)"
          @select="onTrackingLinkSelect"
        />

        <p
          v-if="isExperience && !isMapped('permalink')"
          class="sidebar-help-text"
          data-test="graphics-qrCodeSettings-trackingLinkPartnerNote"
        >
          This defaults to the Experience's partner tracking link. The partner link's
          destination can't be changed, but you can choose a different tracking link.
        </p>
      </div>
    </SidebarSettingsGroup>

    <SidebarSettingsGroup
      title="Style & Animation"
      collapsible
      :element-type="element.type"
      data-test="graphics-qrCodeSettings-styleAndAnimation"
    >
      <div class="sidebar-form-group">
        <div class="settings-row settings-col-xs">
          <label class="sidebar-form-label">Background color</label>
          <colorpicker v-model="backgroundColor" />
        </div>

        <hr class="sidebar-divider" />

        <div class="settings-row settings-col-xs">
          <label class="sidebar-form-label">Dot color</label>
          <colorpicker v-model="dotColor" />
        </div>
      </div>

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">
          Logo image

          <input
            v-model="hasLogo"
            type="checkbox"
            data-test="graphics-qrCodeSettings-toggleLogo"
            data-entity="graphics-qrCodeSettings-toggleLogo"
            @change="removeBackgroundImage"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check"></span>
          </div>
        </label>

        <template v-if="hasLogo">
          <div
            class="image-upload-wrapper"
            :class="{
              'has-image': logoUrl,
              'no-image': !logoUrl,
            }"
          >
            <template v-if="logoUrl">
              <div
                class="chosen-image"
                :style="{
                  backgroundImage: 'url(' + logoUrl + ')',
                }"
                data-test="graphics-qrCodeSettings-chosenImage"
              />
              <div class="chosen-image-actions">
                <button
                  data-balloon="Choose image"
                  data-balloon-pos="up-right"
                  class="btn btn-sm btn-icon choose-image"
                  data-test="graphics-qrCodeSettings-chooseImage"
                  data-entity="graphics-qrCodeSettings-chooseImage"
                @click="selectAsset('image', 'logoUrl')"
              >
                <span class="fa-solid fa-image"></span>
              </button>

                <button
                  data-balloon="Remove image"
                  data-balloon-pos="up-right"
                  class="btn btn-sm btn-icon remove-image"
                  data-test="graphics-qrCodeSettings-removeImage"
                  data-entity="graphics-qrCodeSettings-removeImage"
                  @click="removeBackgroundImage"
                >
                  <span class="fa-solid fa-xmark"></span>
                </button>
              </div>
            </template>

            <template v-else>
              <button
                data-balloon="Choose image"
                data-balloon-pos="up"
                class="btn btn-style-b btn-sm btn-icon choose-image"
                data-test="graphics-qrCodeSettings-chooseImage"
                data-entity="graphics-qrCodeSettings-chooseImage"
                @click="selectAsset('image', 'logoUrl')"
              >
                <span class="fa-solid fa-image-circle-plus" />
              </button>
            </template>
          </div>
        </template>
      </div>

      <hr class="sidebar-divider" />

      <animation-settings :element="element"></animation-settings>
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import SettingsMixins from '../../mixins/settings-mixins';

import MapDataControl from './Data/MapDataControl.vue';

import TrackingLinkPicker from './Data/TrackingLinkPicker.vue';

import AnimationSettings from './Animation.vue';

const isValidUrl = (url) => {
  try {
    // eslint-disable-next-line no-new
    new URL(url);
  } catch (e) {
    return false;
  }

  return true;
};

export default {
  emits: ['toggleSave', 'modal'],
  name: 'QRCodeSettingsComponent',

  components: {
    'animation-settings': AnimationSettings,
    'tracking-link-picker': TrackingLinkPicker,
    MapDataControl,
  },

  mixins: [SettingsMixins],

  props: {
    element: {
      type: Object,
      required: true,
    },

    scrollOffset: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      assetUploaderOpen: false,
      validURL: true,
    };
  },

  computed: {
    isExperience() {
      return !!this.getPropertyValue('experienceId');
    },

    permalink: {
      get() {
        return (this.isMapped('permalink') && this.dataValue('permalink')) || this.element.permalink;
      },
      set(v) {
        this.updateOneElementValue('permalink', v);
      },
    },

    backgroundColor: {
      get() {
        return this.getPropertyValue('backgroundColor');
      },
      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },

    dotColor: {
      get() {
        return this.getPropertyValue('dotColor');
      },

      set(v) {
        this.updateOneElementValue('dotColor', v);
      },
    },
    hasLogo: {
      get() {
        return this.getPropertyValue('hasLogo');
      },
      set(v) {
        this.updateOneElementValue('hasLogo', v);
      },
    },
    logoUrl() {
      return this.getPropertyValue('logoUrl');
    },
  },
  watch: {
    permalink(newVal) {
      this.validURL = isValidUrl(newVal);
      if (this.validURL) {
        // correct a bug in QR code library
        if (newVal.length > 193 && newVal.length < 221) {
          this.permalink = newVal.padEnd(221);
        }
      }
      this.$emit('toggleSave', this.validURL);
    },
  },

  methods: {
    onTrackingLinkSelect(link) {
      // Selecting a tracking link sets the QR destination; the rid is stored on
      // the element so live rendering can compose the static short URL.
      if (link && link.currentDestination && link.currentDestination.destinationUrl) {
        this.permalink = link.currentDestination.destinationUrl;
      }
    },

    removeBackgroundImage() {
      this.updateOneElementValue('logoUrl', '');
    },

    selectAsset(type, setting) {
      this.$emit('modal', { open: true, type });

      window.assetmanfn = ({ asset }) => {
        this.updateOneElementValue(setting, asset?.media_url || '');
        this.$emit('modal', { open: false });
      };
    },
  },
};
</script>
